import { FC, useMemo } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import { ETransactionType } from '@xeppt/xeppt-sdk/types';
import { currencies } from '@const/currency';
import {
    copyStringToClipboard,
    formatDate,
    getFirstLettersBySpace,
    getIntentStatus,
    prettifyAmount
} from '@utils/index';
import { EIntentStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import Typography from '@components/common/typography';
import { EPaymentIntentStatus, TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiTransactionsService } from '@api';

const fullInfoStatuses = [EPaymentIntentStatus.Paid];

const ViewExtenralTransactionModal: FC<
    IModalComponentProps<{ transaction: TPaymentIntent; refetchTransactions?: () => void }>
> = ({ modalData, onClose }) => {
    const { t, i18n } = useTranslation('transaction_details');
    const { submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();

    if (!modalData?.transaction) return null;

    const { transaction, refetchTransactions } = modalData;

    const getAvatar = useMemo(() => {
        return getFirstLettersBySpace(transaction.contact_first_name+ transaction.contact_last_name);
    }, [transaction]);

    const getEmail = useMemo(() => {
        return transaction.contact_email;
    }, [transaction]);

    const getTransactionName = useMemo(() => {
        if (transaction.type === ETransactionType.CREDIT) {
            return 'Send money via XEPPT';
        }
        return 'Send request via XEPPT';
    }, [transaction]);

    const getSenderString = useMemo(() => {
        if (transaction.type === ETransactionType.CREDIT) {
            return {
                neutral: `you send`,
                past: `you sent`,
                amount: transaction?.amount
            };
        }
        return {
            neutral: `you request`,
            past: `you requested`,
            amount: transaction?.amount
        };
    }, [transaction]);

    const getRecipientString = useMemo(() => {
        if (transaction.type === ETransactionType.CREDIT) {
            return {
                neutral: `${transaction.contact_first_name} ${transaction.contact_last_name} receive`,
                past: `${transaction.contact_first_name} ${transaction.contact_last_name} received`,
                amount: transaction?.amount
            };
        }
        // return {
        //     neutral: `${transaction.contact_name} send`,
        //     past: `${transaction.contact_name} sent`,
        //     amount: transaction?.amount
        // };
    }, [transaction]);

    const { handleRequest: handleCancelIntent, isLoading: isCancelIntentLoading } = useApiMutation({
        method: () => apiTransactionsService.cancelPaymentIntent(transaction.id),
        onSuccess: () => {
            refetchTransactions && refetchTransactions();
            requestSuccessLocale('cancel_intent_success');
            onClose();
        },
        onError: requestErrorLocale
    });

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>{getAvatar}</div>
                    <p className={styles.user_name}>{transaction.contact_first_name} {transaction.contact_last_name}</p>
                    <p className={styles.user_email}>{getEmail}</p>
                    <p className={styles.method}>External contact</p>
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    transaction?.type === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {getTransactionName}
                        </Typography>
                    </div>
                    <div className={styles.payment_id}>
                        <div className={styles.id}>
                            <p>Request ID:</p>
                            <p>{transaction.id}</p>
                            <button onClick={() => copyStringToClipboard(transaction.id)}>
                                <Icon name="copy" width={18} height={18} />
                            </button>
                        </div>
                    </div>
                    <Status variant={getIntentStatus(transaction?.status)}>
                        {transaction?.status &&
                            EIntentStatusTranslate[transaction.status]?.[
                                i18n.language as 'en' | 'fr'
                            ]}
                    </Status>
                </div>
                <div className={styles.details}>
                    <div className={styles.row}>
                        <p>Date and time</p>
                        <p>{formatDate(transaction.created_at, 'DD MMMM YYYY, HH:mm')}</p>
                    </div>
                    {getSenderString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== EPaymentIntentStatus.Paid
                                    ? getSenderString?.neutral
                                    : getSenderString?.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getSenderString?.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction?.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction?.status) && getRecipientString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== EPaymentIntentStatus.Paid
                                    ? getRecipientString?.neutral
                                    : getRecipientString?.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {' '}
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getRecipientString?.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction?.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {transaction?.message && (
                        <div className={styles.row}>
                            <p>Message (optional)</p>
                            <p className={styles.row_value}>{transaction?.message || '-'}</p>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.actions_wrapper}>
                <div className={styles.actions}>
                    <Button
                        variant="outlined-red"
                        size="medium"
                        onClick={() => {
                            handleOpenSubmitRequestModal(transaction?.id);
                        }}>
                        {submitLocale('report')}
                    </Button>
                    {transaction?.status === EPaymentIntentStatus.Pending && (
                        <Button
                            variant="red"
                            size="medium"
                            isLoading={isCancelIntentLoading}
                            onClick={() => {
                                handleCancelIntent(undefined);
                            }}>
                            {submitLocale('cancel')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewExtenralTransactionModal;
